/**
 * Algolia Index Configuration
 *
 * This file contains ONLY index name configuration and index lookup utilities.
 * For client initialization and caching, see AlgoliaSearch.ts
 *
 * Exports:
 * - Index name mappings for all 32 indices (30 main + 2 investor)
 * - Index getter functions (getGlobalIndex, getBlogIndex, etc.)
 * - User token management for Algolia Insights
 */

// =============================================================================
// Types
// =============================================================================

export type MainLocale = 'en' | 'zh' | 'ja' | 'de' | 'fr' | 'es';
export type InvestorLocale = 'en' | 'sv';
export type Locale = MainLocale | InvestorLocale;

interface LocaleIndexMap {
  [locale: string]: string;
}

interface MainIndices {
  global: LocaleIndexMap;
  blog: LocaleIndexMap;
  events: LocaleIndexMap;
  resources: LocaleIndexMap;
  products: LocaleIndexMap;
}

interface InvestorIndices {
  global: LocaleIndexMap;
}

export interface AlgoliaIndices {
  main: MainIndices;
  investor: InvestorIndices;
}

// =============================================================================
// Index Configuration
// =============================================================================

/**
 * All Algolia index names (32 total indices)
 * These map directly to the indices created by storyblokAlgoliaSync function
 * Manage Algolia indices here: https://dashboard.algolia.com/apps/YHQ1MK9A38/dashboard
 */
export const ALGOLIA_INDICES: AlgoliaIndices = {
  main: {
    global: {
      en: 'main_en',
      zh: 'main_zh',
      ja: 'main_ja',
      de: 'main_de',
      fr: 'main_fr',
      es: 'main_es',
    },
    blog: {
      en: 'main_en_blog',
      zh: 'main_zh_blog',
      ja: 'main_ja_blog',
      de: 'main_de_blog',
      fr: 'main_fr_blog',
      es: 'main_es_blog',
    },
    events: {
      en: 'main_en_events',
      zh: 'main_zh_events',
      ja: 'main_ja_events',
      de: 'main_de_events',
      fr: 'main_fr_events',
      es: 'main_es_events',
    },
    resources: {
      en: 'main_en_resources',
      zh: 'main_zh_resources',
      ja: 'main_ja_resources',
      de: 'main_de_resources',
      fr: 'main_fr_resources',
      es: 'main_es_resources',
    },
    products: {
      en: 'main_en_products',
      zh: 'main_zh_products',
      ja: 'main_ja_products',
      de: 'main_de_products',
      fr: 'main_fr_products',
      es: 'main_es_products',
    },
  },
  investor: {
    global: {
      en: 'investor_en',
      sv: 'investor_sv',
    },
  },
};

// =============================================================================
// Validation
// =============================================================================

/**
 * Validate that all required indices are configured
 * Throws error if any required index name is missing
 */
function validateIndices(): void {
  const errors: string[] = [];

  // Check main global indices
  const mainLocales: MainLocale[] = ['en', 'zh', 'ja', 'de', 'fr', 'es'];
  mainLocales.forEach(locale => {
    if (!ALGOLIA_INDICES.main.global[locale]) {
      errors.push(`Missing main global index for ${locale}`);
    }
    if (!ALGOLIA_INDICES.main.blog[locale]) {
      errors.push(`Missing main blog index for ${locale}`);
    }
    if (!ALGOLIA_INDICES.main.events[locale]) {
      errors.push(`Missing main events index for ${locale}`);
    }
    if (!ALGOLIA_INDICES.main.resources[locale]) {
      errors.push(`Missing main resources index for ${locale}`);
    }
    if (!ALGOLIA_INDICES.main.products[locale]) {
      errors.push(`Missing main products index for ${locale}`);
    }
  });

  // Check investor indices
  const investorLocales: InvestorLocale[] = ['en', 'sv'];
  investorLocales.forEach(locale => {
    if (!ALGOLIA_INDICES.investor.global[locale]) {
      errors.push(`Missing investor global index for ${locale}`);
    }
  });

  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('Algolia index configuration warnings:', errors);
    // Don't throw - allow fallback to defaults
  }
}

// Run validation on module load
validateIndices();

// =============================================================================
// Index Getters
// =============================================================================

/**
 * Get main global search index for a locale
 * Used for: Cross-content type searches, global site search
 */
export const getGlobalIndex = (locale: string): string => {
  return ALGOLIA_INDICES.main.global[locale] || ALGOLIA_INDICES.main.global.en;
};

/**
 * Get blog-specific index for a locale
 * Used for: Blog/article finder pages
 */
export const getBlogIndex = (locale: string): string => {
  return ALGOLIA_INDICES.main.blog[locale] || ALGOLIA_INDICES.main.blog.en;
};

/**
 * Get events-specific index for a locale
 * Used for: Event finder pages
 */
export const getEventsIndex = (locale: string): string => {
  return ALGOLIA_INDICES.main.events[locale] || ALGOLIA_INDICES.main.events.en;
};

/**
 * Get resources-specific index for a locale
 * Used for: Resource finder pages
 */
export const getResourcesIndex = (locale: string): string => {
  return ALGOLIA_INDICES.main.resources[locale] || ALGOLIA_INDICES.main.resources.en;
};

/**
 * Get products-specific index for a locale
 * Used for: Product finder pages
 */
export const getProductsIndex = (locale: string): string => {
  return ALGOLIA_INDICES.main.products[locale] || ALGOLIA_INDICES.main.products.en;
};

/**
 * Get investor global index for a locale
 * Used for: Investor site searches
 */
export const getInvestorIndex = (locale: string): string => {
  return ALGOLIA_INDICES.investor.global[locale] || ALGOLIA_INDICES.investor.global.en;
};

// =============================================================================
// User Token Management
// =============================================================================

let cachedToken: string | null = null;

/**
 * Get or create a persistent user token for Algolia Insights
 * Tokens are stored in localStorage for consistency across sessions
 */
export const getAlgoliaUserToken = (): string => {
  if (cachedToken) {
    return cachedToken;
  }

  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem('algolia_user_token');
      if (stored) {
        cachedToken = stored;
        return stored;
      }
    } catch (error) {
      // localStorage not available, continue with generation
    }
  }

  // Generate new token using cryptographically secure randomness
  // Prefer crypto.randomUUID() for better security, fallback to Date + Math.random()
  const newToken =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? `user-${crypto.randomUUID()}`
      : `anonymous-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('algolia_user_token', newToken);
    } catch (error) {
      // localStorage not available, token will be ephemeral
    }
  }

  cachedToken = newToken;
  return newToken;
};
